import { useState } from "react";
import { CheckCircle2, XCircle, AlertCircle } from "lucide-react";
import { Reveal } from "./Reveal";
import { useSite } from "../context/SiteContext";
import { MARKSHEET_YEARS } from "../data/site";

function statusOf(m) {
  if (m.collected) return { label: "Collected", cls: "text-forest-500", Icon: CheckCircle2 };
  if (m.available) return { label: "Come & Collect", cls: "text-gold", Icon: AlertCircle };
  return { label: "Not available yet", cls: "text-muted", Icon: XCircle };
}

export default function Marksheet() {
  const { marksheets } = useSite();
  const [year, setYear] = useState(MARKSHEET_YEARS[0]);
  const shown = (marksheets || []).filter((m) => m.year === year);

  return (
    <section data-testid="marksheet-section" className="bg-bone py-20 md:py-28">
      <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-16">
        <Reveal>
          <p className="text-xs uppercase tracking-[0.3em] text-gold mb-4">Examination</p>
          <h2 className="font-serif text-4xl md:text-6xl tracking-tight text-forest leading-none">Marksheet &amp; Documents</h2>
          <p className="text-muted mt-5 max-w-xl">Check whether your marksheet or any document is ready. When the status shows <span className="text-gold font-medium">Come &amp; Collect</span>, please visit the college office to collect it.</p>
        </Reveal>

        <div className="flex items-center gap-2 mt-10 mb-6 flex-wrap">
          {MARKSHEET_YEARS.map((y) => (
            <button key={y} data-testid="marksheet-year-tab" onClick={() => setYear(y)} className={`text-sm px-4 py-2 rounded-full border transition-colors ${year === y ? "bg-forest text-bone border-forest" : "border-line text-muted hover:border-forest"}`}>
              {y}
            </button>
          ))}
        </div>

        <div className="border border-line bg-white overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-forest text-bone/80 uppercase text-xs tracking-widest">
              <tr>
                <th className="text-left px-5 py-3 w-12">#</th>
                <th className="text-left px-5 py-3">Name of Student</th>
                <th className="text-left px-5 py-3">ABC ID</th>
                <th className="text-left px-5 py-3">Document</th>
                <th className="text-left px-5 py-3">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-line">
              {shown.map((m, i) => {
                const st = statusOf(m);
                return (
                  <tr key={m.id || i} data-testid="marksheet-view-row" className="hover:bg-bone/60 transition-colors">
                    <td className="px-5 py-3 font-mono text-muted">{String(i + 1).padStart(2, "0")}</td>
                    <td className="px-5 py-3 font-medium text-forest">{m.name}</td>
                    <td className="px-5 py-3 font-mono text-forest">{m.abc_id || "—"}</td>
                    <td className="px-5 py-3 text-muted">{m.document || "Marksheet"}</td>
                    <td className="px-5 py-3">
                      <span data-testid="marksheet-status" className={`inline-flex items-center gap-1.5 text-xs font-medium ${st.cls}`}>
                        <st.Icon size={15} /> {st.label}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {shown.length === 0 && <p className="p-8 text-muted">No document records published for {year} yet.</p>}
        </div>
      </div>
    </section>
  );
}
